import React, { useMemo, useContext, useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Brand from './Brand';
import Menu from './Menu';
import { AuthContext } from '../../../../contexts/AuthContext';
import { Menus } from '../../../../utils/appConstant';
import { hasResourseAccess } from '../../../../utils/methods';
import { Modules, AuthRoutes } from '../../../../enums';

interface SidebarProps {
  collapsed: boolean;
  onCollapse: (_: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ collapsed, onCollapse }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { permissions, isSystemAdmin, loggedInUser, logout } = useContext(AuthContext);
  const [footMenuOpen, setFootMenuOpen] = useState<boolean>(false);
  const asideRef = useRef<HTMLLIElement>(null);

  useEffect(() => {
    const root = document.documentElement;

    if (root.clientWidth < 1200) {
      const handleClickOutside = (event: MouseEvent) => {
        if (asideRef.current && !asideRef.current.contains(event.target as Node)) {
          onCollapse(true);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [onCollapse]);

  const sideMenus = useMemo(() => {
    return Menus.filter((menu) => {
      return hasResourseAccess(
        isSystemAdmin,
        permissions,
        menu.accessibleTo || menu.childrens ? menu : null
      );
    });
  }, [Menus, permissions, isSystemAdmin]);

  const sidebarExpanded = !collapsed;

  return (
    <aside
      ref={asideRef}
      id="layout-menu"
      className={`obs-sidebar ${collapsed ? 'obs-sidebar-rail' : ''}`}
    >
      <Brand sidebarExpanded={sidebarExpanded} onToggleCollapse={() => onCollapse(!collapsed)} />

      <div className="menu-inner menu-inner-custom obs-nav-list">
        {sideMenus.map((sideMenu) => (
          <React.Fragment key={sideMenu.id}>
            {sideMenu.sectionLabel && (
              <div className="obs-nav-section-label">
                {sideMenu.sectionLabel}
              </div>
            )}
            <Menu {...sideMenu} sidebarExpanded={sidebarExpanded} />
          </React.Fragment>
        ))}
      </div>

      <div className={`obs-sidebar-footer position-relative border-top w-100 ${collapsed ? 'd-flex justify-content-center p-2' : ''}`}>
        {footMenuOpen && (
          <>
            <div className="position-fixed top-0 bottom-0 start-0 end-0 z-40" onClick={() => setFootMenuOpen(false)} style={{ zIndex: 1090 }} />
            <div
              className="z-50 border p-1"
              style={{
                position: collapsed ? 'fixed' : 'absolute',
                bottom: collapsed ? '16px' : 'calc(100% + 8px)',
                left: collapsed ? '82px' : '12px',
                right: collapsed ? 'auto' : '12px',
                width: collapsed ? '224px' : 'auto',
                zIndex: 1095,
                backgroundColor: 'var(--surf)',
                borderColor: 'var(--line2)',
                boxShadow: 'var(--shadow)',
                borderRadius: 'var(--radius-ctl, 12px)',
              }}
            >
              <button
                type="button"
                className="w-100 border-0 px-3 py-2 text-start rounded-3"
                style={{
                  fontSize: '13.5px',
                  fontFamily: 'var(--font-sans)',
                  color: 'var(--txt)',
                  backgroundColor: 'transparent',
                  transition: 'background-color 0.15s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--surf2)')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                onClick={() => {
                  setFootMenuOpen(false);
                  navigate(`${Modules.my}${AuthRoutes.account}?tab=profile`);
                }}
              >
                {t('account.nav.profile') || 'My Profile'}
              </button>
              <button
                type="button"
                className="w-100 border-0 px-3 py-2 text-start rounded-3"
                style={{
                  fontSize: '13.5px',
                  fontFamily: 'var(--font-sans)',
                  color: 'var(--txt)',
                  backgroundColor: 'transparent',
                  transition: 'background-color 0.15s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--surf2)')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                onClick={() => {
                  setFootMenuOpen(false);
                  navigate(`${Modules.my}${AuthRoutes.account}?tab=security`);
                }}
              >
                {t('account.nav.security') || 'Change Password'}
              </button>
              <button
                type="button"
                className="w-100 border-0 px-3 py-2 text-start rounded-3"
                style={{
                  fontSize: '13.5px',
                  fontFamily: 'var(--font-sans)',
                  color: 'var(--txt)',
                  backgroundColor: 'transparent',
                  transition: 'background-color 0.15s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--surf2)')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                onClick={() => {
                  setFootMenuOpen(false);
                  navigate(`${Modules.my}${AuthRoutes.account}?tab=sessions`);
                }}
              >
                {t('account.nav.sessions') || 'Sessions'}
              </button>
              <button
                type="button"
                className="w-100 border-0 px-3 py-2 text-start rounded-3"
                style={{
                  fontSize: '13.5px',
                  fontFamily: 'var(--font-sans)',
                  color: 'var(--txt)',
                  backgroundColor: 'transparent',
                  transition: 'background-color 0.15s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--surf2)')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                onClick={() => {
                  setFootMenuOpen(false);
                  navigate(`${Modules.my}${AuthRoutes.account}?tab=preferences`);
                }}
              >
                {t('account.nav.preferences') || 'Settings'}
              </button>
              <div style={{ height: '1px', backgroundColor: 'var(--line)', margin: '6px 8px' }} />
              <button
                type="button"
                className="w-100 border-0 px-3 py-2 text-start rounded-3"
                style={{
                  fontSize: '13.5px',
                  fontFamily: 'var(--font-sans)',
                  color: 'var(--red)',
                  backgroundColor: 'transparent',
                  transition: 'background-color 0.15s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--red-dim)')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                onClick={async () => {
                  setFootMenuOpen(false);
                  await logout();
                  navigate(Modules.auth);
                }}
              >
                {t('layout.pageTexts.private.topbar.profileDropdown.logout') || 'Sign out'}
              </button>
            </div>
          </>
        )}
        <div
          role="button"
          onClick={() => setFootMenuOpen((prev) => !prev)}
          className={`obs-sidenav-user d-flex align-items-center p-3 cursor-pointer w-100 ${sidebarExpanded ? 'gap-3' : 'justify-content-center position-relative'}`}
          style={{ transition: 'background-color 0.15s' }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--surf)')}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
        >
          <div
            className="obs-avatar rounded-circle d-flex align-items-center justify-content-center fw-bold flex-shrink-0"
            style={{ width: 38, height: 38, backgroundColor: 'var(--surf3)', border: '1px solid var(--line2)', color: 'var(--acc-text, var(--txt))', fontFamily: 'var(--font-serif)', fontSize: 13 }}
          >
            {loggedInUser?.full_name 
              ? loggedInUser.full_name.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase() 
              : loggedInUser?.email?.slice(0, 2).toUpperCase() ?? 'US'}
          </div>
          {sidebarExpanded ? (
            <div className="obs-user-info overflow-hidden min-w-0">
              <div className="fw-semibold text-truncate text-start" style={{ color: 'var(--txt)', fontSize: '13.5px' }}>
                {loggedInUser?.full_name ?? loggedInUser?.email ?? 'User'}
              </div>
              <div className="text-muted text-truncate text-start" style={{ color: 'var(--dim)', fontSize: '11.5px' }}>
                {loggedInUser?.roles?.[0]?.name ?? 'User'}
              </div>
            </div>
          ) : (
            <span className="obs-flyout">{loggedInUser?.full_name ?? loggedInUser?.email ?? 'User'}</span>
          )}
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
